import { ApplicationTaskModel, BorrowerTaskType } from 'projects/borrower-app/src/app/models/borrower-portal/application-task-model';

export type TaskTextField = 'docType' | 'description' | 'borrowerFacingNote';

/**
 * LLS-886: fixed English the Api writes or seeds onto these loan tasks itself (not lender-entered). Shown in Spanish via
 * portal strings only while the stored text is still exactly this default; an edited value shows as typed.
 */
const SYSTEM_TASK_TEXT: ReadonlyArray<{ type: BorrowerTaskType; field: TaskTextField; english: string; key: string }> = [
  { type: BorrowerTaskType.OnlineApplication, field: 'description', english: 'Please complete your online application', key: 'portal.tasks.system.onlineAppNote' },
  { type: BorrowerTaskType.OnlineApplication, field: 'borrowerFacingNote', english: 'Please complete your online application', key: 'portal.tasks.system.onlineAppNote' },
  { type: BorrowerTaskType.RequestHoi, field: 'description', english: 'Here are the Homeowners Insurance Quotes you requested!', key: 'portal.tasks.system.hoiQuotesTitle' },
  { type: BorrowerTaskType.MiQuote, field: 'description', english: 'Here are the Mortgage Insurance Quotes you requested!', key: 'portal.tasks.system.miQuotesTitle' },
];

/** Lender Spanish (_i18n) first, then our portal string for fixed system text, then the stored text. */
export function resolveTaskText(
  task: ApplicationTaskModel | null | undefined,
  field: TaskTextField,
  lang: string | null | undefined,
  translate: (key: string) => string,
): string | undefined {
  if (!task) {
    return undefined;
  }
  const stored = task[field] ?? undefined;
  if (!lang || lang === 'en') {
    return stored;
  }
  const lenderText = task._i18n?.[lang]?.[field];
  if (lenderText) {
    return lenderText;
  }
  const system = lang !== 'es' ? undefined : SYSTEM_TASK_TEXT.find(s => s.type === task.borrowerTaskType && s.field === field && stored?.trim() === s.english);
  return system ? translate(system.key) : stored;
}

/**
 * The description to show under the title, or undefined when it would only repeat the title. Compares the displayed
 * text, not the stored English: a task and its document type have separate Spanish names.
 */
export function resolveDistinctTaskDescription(
  task: ApplicationTaskModel | null | undefined,
  lang: string | null | undefined,
  translate: (key: string) => string,
): string | undefined {
  const description = resolveTaskText(task, 'description', lang, translate);
  const docType = resolveTaskText(task, 'docType', lang, translate);
  return description && docType && description !== docType ? description : undefined;
}
